import { CommonModule } from '@angular/common';
import { Component, ElementRef, Renderer2, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthServiceService } from '../../services/authService/auth-service.service';
import { SourceTextModule } from 'vm';
import { NotificationRequest } from '../../models/notification-request';
import { NotificationService } from '../../services/notification/notification.service';
import { SharedService } from '../../services/shared/shared.service';
import { user } from '@angular/fire/auth';
import { userInfo } from 'os';
import { FirestoreService } from '../../services/firestore/firestore.service';
import { UserDto } from '../../models/user-dto';
import { Timestamp } from '@angular/fire/firestore';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [RouterOutlet, CommonModule, RouterLinkActive, RouterLink],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.css'
})
export class AdminComponent implements AfterViewInit {
  @ViewChild('layoutMenu') layoutMenu!: ElementRef;
  @ViewChild('menuToggle', { static: false }) menuToggle!: ElementRef;
  @ViewChild('menuToggles', { static: false }) menuToggles!: ElementRef;

  isAdmin = false;
  isMenuOpen = false; // Closed by default on mobile
  showSpinner = true;
  userUid: any;
  notifications = new Array<NotificationRequest>();
  notificationCount = 0; 
  userInfo: UserDto = {
    id: '',
    email: '',
    fullName: '',
  }

  constructor(
    private renderer: Renderer2,
    private authService: AuthServiceService,
    private router: Router,
    private notificationService: NotificationService,
    public sharedService: SharedService,
    private firestoreService: FirestoreService
  ) {}

  ngOnInit() {
    this.checkAdmin();
    this.userUid = this.authService.getCurrentUserUid();
    this.getUserInfo();
    this.getNewNotification(this.userUid);
    this.getNotifications(this.userUid);
  }

  checkAdmin(): void {
    if (!navigator.onLine) {
      // 🚨 Offline mode
      this.sharedService.handleError('Keine Internetverbindung');
      this.showSpinner = false;
      // Add a slight delay to ensure logging/error handling completes
      setTimeout(() => {
        this.router.navigateByUrl('/Home');
      }, 1000); // 1-second delay
      return;
    }

    // ✅ Online → call Firebase
    this.authService.isAdmin().subscribe({
      next: (res) => {
        this.showSpinner = false;
        this.isAdmin = res;
        if (!this.isAdmin) {
          this.router.navigateByUrl('/Home');
        }
      },
      error: (err) => {
        this.sharedService.handleError(err);
        this.showSpinner = false;
        this.router.navigateByUrl('/Home');
      }
    });
  }

  getUserInfo() {
    this.firestoreService.getById("users", this.userUid).subscribe({
      next: res => this.userInfo = res,
      error: err => {
        this.sharedService.handleError(err);
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMenuToggler();
    this.addMenuHoverEffect();
    this.addDropdownToggle();
    this.initOverlay();
  }

  initMenuToggler() {
    if (this.menuToggle?.nativeElement) {
      this.renderer.listen(this.menuToggle.nativeElement, 'click', (event: Event) => {
        event.preventDefault();
        this.toggleMenu();
      });
    } 

    if (this.menuToggles?.nativeElement) {
      this.renderer.listen(this.menuToggles.nativeElement, 'click', (event: Event) => {
        event.preventDefault();
        this.toggleMenu();
      });
    } 
  }

  initOverlay() {
    const overlayElement = document.querySelector('.layout-overlay');
    if (overlayElement) {
      this.renderer.listen(overlayElement, 'click', () => {
        this.isMenuOpen = false;
        this.renderer.removeClass(this.layoutMenu.nativeElement, 'open');
        this.renderer.removeClass(overlayElement, 'd-block');
      });
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    const menuElement = this.layoutMenu.nativeElement;
    const overlayElement = document.querySelector('.layout-overlay');

    if (this.isMenuOpen) {
      this.renderer.addClass(menuElement, 'open');
      if (overlayElement) {
        this.renderer.addClass(overlayElement, 'd-block');
      }
    } else {
      this.renderer.removeClass(menuElement, 'open');
      if (overlayElement) {
        this.renderer.removeClass(overlayElement, 'd-block');
      }
    }
  }

  addMenuHoverEffect() {
    if (this.layoutMenu) {
      const menu = this.layoutMenu.nativeElement;
      let timeout: any;

      this.renderer.listen(menu, 'mouseenter', () => {
        if (!this.isSmallScreen() && !this.isMenuOpen) {
          timeout = setTimeout(() => {
            this.renderer.addClass(menu, 'd-block');
          }, 300);
        }
      });

      this.renderer.listen(menu, 'mouseleave', () => {
        if (!this.isSmallScreen() && !this.isMenuOpen) {
          clearTimeout(timeout);
          this.renderer.removeClass(menu, 'd-block');
        }
      });
    }
  }

  addDropdownToggle() {
    if (this.layoutMenu) {
      const toggles = this.layoutMenu.nativeElement.querySelectorAll('.toggle-dropdown');
      toggles.forEach((toggleEl: HTMLElement) => {
        this.renderer.listen(toggleEl, 'click', (event: Event) => {
          event.preventDefault();
          const parentNode = toggleEl.parentNode as HTMLElement;
          const nextElement = parentNode?.nextElementSibling as HTMLElement;

          parentNode?.classList.toggle('active');
          nextElement?.classList.toggle('dropdown-active');
          event.stopImmediatePropagation();
        });
      });
    }
  }

  isSmallScreen(): boolean {
    return window.innerWidth < 1200; // Match Bootstrap xl breakpoint
  }

  logout() {
    this.authService.signOut().subscribe({
      next: () => this.router.navigateByUrl('/Home'),
      error: () => this.router.navigateByUrl('/Home')
    });
  }

  getNotifications(userUid: string) {
    this.notificationService.getNotificationsByUserUid(userUid).subscribe({
      next: res => {
        this.notifications = res;
        if (this.notifications.length > 5) {
          this.notifications = this.notifications.slice(0, 5);
        }
      },
    });
  }

  getNewNotification(userUid: string) {
    this.notificationService.getNewNotificationCount(userUid).subscribe({
      next: res => this.notificationCount = res
    });
  }

  updateAllNotifications(userUid: string) {
    this.notificationService.updateNotificationsToNotNew(userUid).subscribe({
      next: () => {
        console.log('Alle neuen Benachrichtigungen als nicht neu markiert');
      },
      error: (err) => {
        console.error('Fehler beim Aktualisieren der Benachrichtigungen:', err);
      }
    });
  }

  toggleDropdown(notification: any) {
    this.notifications.forEach(n => n.isOpen = n === notification ? !n.isOpen : false);
  }

  markAsRead(notification: any) {
    notification.status = 'READ';
    notification.isOpen = false;
    this.notificationService.markNotificationAsRead(this.userUid, notification.id);
  }

  deleteNotification(notification: any) {
    this.notificationService.deleteNotification(this.userUid, notification.id);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.position-relative')) {
      this.notifications.forEach(n => n.isOpen = false);
    }
  }

  logOut() {
    this.authService.signOut().subscribe({
      next: res => {
        this.router.navigateByUrl("/Home");
  
        // ✅ Proper logout alert instead of "Login successfully"
        Swal.fire({
          icon: "info",
          title: "Abgemeldet",
          text: "Sie wurden erfolgreich abgemeldet.",
          confirmButtonText: "OK"
        });
      },
      error: err => {
        this.sharedService.handleError(err);
      }
    });
  }
}